import { useCallback, useEffect, useRef, useState } from "react";
import type { RecipeCostReport } from "../../../shared/production/recipe-cost";
import { apiRequest } from "../../shared/api/client";
import { todayInIstanbul } from "./production-report-format";
import {recipeKindValid} from "./recipe-cost-presentation";
import type { PlannedProduct } from "./production-recipe-cost-plan";

export function useRecipeCostCalculation(kind: string) {
  const [selected, setSelected] = useState<PlannedProduct | null>(null);
  const [report, setReport] = useState<RecipeCostReport | null>(null);
  const [loading, setLoading] = useState(false), [error, setError] = useState("");
  const version = useRef(0), mounted = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const cache = useRef(new Map<string, RecipeCostReport>());
  const inFlight = useRef<string | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; version.current++; controller.current?.abort(); };
  }, []);
  const clear = useCallback(() => {
    version.current++; controller.current?.abort(); inFlight.current = null;
    if (mounted.current) { setSelected(null); setReport(null); setError(""); setLoading(false); }
  }, []);
  const calculate = async (product: PlannedProduct, force = false) => {
    if (!mounted.current || !product.date || product.date > todayInIstanbul() || !recipeKindValid(kind)) return;
    const params = new URLSearchParams({ productCode: product.code, date: product.date, recipeKind: kind });
    const key = params.toString();
    if (inFlight.current === key) return;
    const current = ++version.current;
    controller.current?.abort(); controller.current = new AbortController();
    setSelected(product); setError("");
    if (force) cache.current.delete(key);
    const cached = cache.current.get(key);
    if (!force && cached) { inFlight.current = null; setReport(cached); setLoading(false); return; }
    inFlight.current = key; setReport(null); setLoading(true);
    try {
      const result = await apiRequest<RecipeCostReport>(`/api/production/reports/recipe-cost?${params}`, { cache: "no-store", signal: controller.current.signal });
      if (current !== version.current) return;
      cache.current.set(key, result);
      if (cache.current.size > 40) {
        const oldest = cache.current.keys().next();
        if (!oldest.done) cache.current.delete(oldest.value);
      }
      setReport(result);
    } catch (cause) {
      if (current === version.current) setError(cause instanceof Error ? cause.message : "Hesap tamamlanamadı.");
    } finally {
      if (current === version.current) { inFlight.current = null; setLoading(false); }
    }
  };
  return { selected, report, loading, error, setError, clear, calculate };
}
